import React, { useState } from 'react';
import type { Size } from '@wix/design-system';
import { Box, Cell, Heading, Layout, Text } from '@wix/design-system';
import { withEssentialsContext } from '../../contexts/essentials-context';
import { AppGrid } from '../app-grid';
import type { AppGroup } from '@wix/ambassador-appmarket-v1-app-market-client-aggregator/types';
import { AppPromoGrid } from '../app-promo-grid';
import type {
  ExternalComponentProps,
  ComponentsWithSiteContextProps,
} from '../types';
import { VisibilityDetector } from '../visibility-detector';
import { appsSectionDataHooks } from './data-hooks';
import { useSiteContext, withSiteContext } from '../../contexts/site-context';
import { ViewAllAppsButton } from './view-all-apps-button';
import s from './apps-section.module.scss';
import type { BaseApp } from '../../types';
import { SeeAllButton } from './see-all-button';
import type { AppBoxSkin } from '../../components/app-box/app-box.types';
import { OnMarketClick } from './on-market-click';
import type { AppsSectionButtonType } from '@wix/app-market-components-types';

export interface AppsSectionProps
  extends Required<Omit<AppGroup, 'rows' | 'tagId' | 'appIds'>>,
    ExternalComponentProps,
    ComponentsWithSiteContextProps {
  titleSize?: Size;
  onButtonClick?: () => void;
  columns?: 1 | 2 | 3 | 4;
  minColumns?: 1 | 2 | 3 | 4;
  rows?: number;
  dataHook?: string;
  onAppClick?(app: BaseApp, appIndex: number, impressionId: string): void;
  onAppLoad?(app: BaseApp, appIndex: number, impressionId: string): void;
  onSectionLoad?(): void;
  showBorder?: boolean;
  getHref?(appSlug: string, appIndex: number): string | undefined;
  buttonHref?: string;
  buttonType?: AppsSectionButtonType;
  skin?: AppBoxSkin;
  showTitle?: boolean;
}

function AppsSectionComponent({
  experiments,
  onButtonClick,
  onAppClick,
  columns = 3,
  minColumns,
  titleSize = 'large',
  rows,
  dataHook,
  onAppLoad,
  onSectionLoad,
  showBorder,
  getHref,
  buttonHref,
  languageCode,
  skin = 'default',
  buttonType = 'viewAll',
  showTitle = true,
  ...appGroupProps
}: AppsSectionProps) {
  const { title, apps, type, totalAppsCount } = appGroupProps;
  const { isPremiumSite } = useSiteContext();
  const [finalColumnsCount, setFinalColumnsCount] = useState<number>(columns);
  const appBoxesInSectionCount = finalColumnsCount * (rows ?? Infinity);
  const showViewAllAppsButton =
    onButtonClick &&
    skin === 'default' &&
    totalAppsCount > appBoxesInSectionCount;
  const showSeeAllButton =
    onButtonClick && buttonType === 'seeAll' && skin === 'slim';
  const showMarketButton =
    onButtonClick && buttonType === 'market' && skin === 'slim';

  return (
    <div data-hook={appsSectionDataHooks.container}>
      <Box
        direction="vertical"
        gap={titleSize === 'large' ? '18px' : '12px'}
        dataHook={dataHook}
      >
        {showTitle && (
          <Box direction="vertical">
            <Layout>
              <Cell span={10}>
                {skin === 'slim' ? (
                  <Text
                    size="small"
                    weight="bold"
                    dataHook={appsSectionDataHooks.title}
                  >
                    {title}
                  </Text>
                ) : (
                  <Heading
                    size={titleSize}
                    dataHook={appsSectionDataHooks.title}
                  >
                    {title}
                  </Heading>
                )}
              </Cell>
              <Cell span={2}>
                {showViewAllAppsButton && (
                  <Box
                    className={s.topButton}
                    align="right"
                    verticalAlign="bottom"
                    height="100%"
                  >
                    <ViewAllAppsButton
                      onClick={onButtonClick}
                      href={buttonHref}
                      appsCount={totalAppsCount}
                    />
                  </Box>
                )}
                {showSeeAllButton && (
                  <Box
                    className={s.topButton}
                    align="right"
                    verticalAlign="bottom"
                    height="100%"
                  >
                    <SeeAllButton onClick={onButtonClick} href={buttonHref} />
                  </Box>
                )}
                {showMarketButton && (
                  <Box
                    className={s.topButton}
                    align="right"
                    verticalAlign="bottom"
                    height="100%"
                  >
                    <OnMarketClick onClick={onButtonClick} href={buttonHref} />
                  </Box>
                )}
              </Cell>
            </Layout>
          </Box>
        )}
        <VisibilityDetector
          onFirstVisible={onSectionLoad}
          style={{ height: '100%' }}
        >
          {type === 'PROMO' ? (
            <AppPromoGrid
              dataHook={`${dataHook}-promo-grid`}
              apps={apps}
              experiments={experiments}
              onAppClick={onAppClick}
              onAppLoad={onAppLoad}
              getHref={getHref}
              isPremiumSite={isPremiumSite}
              languageCode={languageCode}
            />
          ) : (
            <AppGrid
              dataHook={`${dataHook}-app-grid`}
              apps={apps}
              experiments={experiments}
              onAppClick={onAppClick}
              columns={columns}
              rows={rows}
              minColumns={minColumns}
              onAppLoad={onAppLoad}
              getHref={getHref}
              isPremiumSite={isPremiumSite}
              languageCode={languageCode}
              showBorder={showBorder}
              finalColumnsCount={(finalColumns: number) =>
                setFinalColumnsCount(finalColumns)
              }
              skin={skin}
            />
          )}
        </VisibilityDetector>
        {showViewAllAppsButton && (
          <Box className={s.bottomButton}>
            <ViewAllAppsButton
              onClick={onButtonClick}
              href={buttonHref}
              appsCount={totalAppsCount}
            />
          </Box>
        )}
        {showSeeAllButton && (
          <Box className={s.bottomButton}>
            <SeeAllButton onClick={onButtonClick} href={buttonHref} />
          </Box>
        )}
        {showMarketButton && (
          <Box className={s.bottomButton}>
            <OnMarketClick onClick={onButtonClick} href={buttonHref} />
          </Box>
        )}
      </Box>
    </div>
  );
}

export const AppsSection = withSiteContext(
  withEssentialsContext(AppsSectionComponent),
);
